import { GovAccordion, GovAccordionItem } from '@gov-design-system-ce/react';
import { isVoterRegistrationSubmission } from '@gov-nx/api';
import { MySubmissionsMessageSkeleton } from '@gov-nx/component/web';
import { useTranslationWithNamespace } from '@gov-nx/core/service';
import { addWhen, pipe } from '@gov-nx/core/types';
import {
	useMojePodaniDatovaZpravaInstance,
	useMojePodaniDetailContextInstance,
} from '@gov-nx/module/page';
import {
	ContentLayout,
	DataTable,
	InfoTooltip,
	LayoutGap,
	ToggleButton,
} from '@gov-nx/ui/web';
import { ConnectToAnotherSubmission } from './ConnectToAnotherSubmission';
import { DisconnectFromSubmission } from './DisconnectFromSubmission';
import { MySubmissionAttachments } from './MySubmissionAttachments';
import { MySubmissionPreview } from './MySubmissionPreview';
import { VoterRegistrationMessage } from './VoterRegistrationMessage';
import { CopyOrEmpty, Row } from './components';

export const MySubmissionMessage = () => {
	const { submission, code, powerOfAttorney } =
		useMojePodaniDetailContextInstance();
	const {
		query,
		dataMessageDataboxId,
		dataMessage,
		display,
		message,
		datumACasDodani,
		toggleAccordeon,
	} = useMojePodaniDatovaZpravaInstance();
	const {
		submissionConnection: { isConnected, disconnect, connect },
	} = useMojePodaniDetailContextInstance();

	const { getLocalizeCurried } = useTranslationWithNamespace();
	const ts = getLocalizeCurried(code);

	return (
		<li>
			<ContentLayout>
				<GovAccordion size="xl">
					<GovAccordionItem
						open={display.isTrue}
						onChange={toggleAccordeon}
						size="xl"
						label={<h3 className="text-headline-s!">{datumACasDodani}</h3>}
						aria-label={pipe(
							[ts('akce.wcag.zobrazit-komunikaci')],
							addWhen(!!datumACasDodani, datumACasDodani),
							(chunks) => chunks.join(' ')
						)}>
						{query.isFetching ? <MySubmissionsMessageSkeleton /> : null}
						{dataMessage ? (
							<LayoutGap
								gap="xl"
								className="px-(--templates-margin-s) py-(--templates-margin-m) mt-0!">
								<LayoutGap gap="m">
									<MySubmissionPreview
										code={code}
										dataMessage={dataMessage}
										submission={submission}
									/>
									<ToggleButton>
										<LayoutGap gap="m">
											<DataTable
												responsive
												customClasses="mt-3 mb-4">
												<tbody>
													<Row label={ts('data.id-datove-zpravy')}>
														<CopyOrEmpty
															value={dataMessage.datovaZpravaId}
															label={ts('akce.zkopirovat-id-datovky')}
														/>
													</Row>
													<tr>
														<td className="sm:w-1/2">
															{ts('data.do-vlastnich-rukou')}
														</td>
														<td className="flex flex-row items-center flex-1 sm:w-1/2">
															<strong>
																{dataMessage.doVlastnichRukou
																	? ts('data.do-vlastnich-rukou-ano')
																	: ts('data.do-vlastnich-rukou-ne')}
															</strong>
															<InfoTooltip
																placement="right"
																content={ts('zprava.info')}
															/>
														</td>
													</tr>
													<Row label={ts('data.k-rukam')}>
														{dataMessage.kRukam ?? '-'}
													</Row>
													<Row label={ts('data.nase-cislo-jednaci')}>
														<CopyOrEmpty
															value={dataMessage.naseCisloJednaci}
															label={ts('akce.zkopirovat-nase-cislo-jednaci')}
														/>
													</Row>
													<Row label={ts('data.nase-spisova-znacka')}>
														<CopyOrEmpty
															value={dataMessage.naseSpisovaZnacka}
															label={ts('akce.zkopirovat-nasi-spisovou-znacku')}
														/>
													</Row>
													<Row label={ts('data.vase-cislo-jednaci')}>
														<CopyOrEmpty
															value={dataMessage.vaseCisloJednaci}
															label={ts('akce.zkopirovat-vase-cislo-jednaci')}
														/>
													</Row>
													<Row label={ts('data.vase-spisova-znacka')}>
														<CopyOrEmpty
															value={dataMessage.vaseSpisovaZnacka}
															label={ts('akce.zkopirovat-vasi-spisovou-znacku')}
														/>
													</Row>
												</tbody>
											</DataTable>
											<footer>
												{isConnected && submission?.id ? (
													<DisconnectFromSubmission
														{...disconnect}
														variables={{
															datovaSchrankaId:
																message.dataMessage.datovaSchrankaId,
															datovaZpravaId:
																message.dataMessage.datovaZpravaId,
															submissionId: submission.id,
														}}
														buttonProps={{
															type: 'link',
														}}
													/>
												) : (
													<ConnectToAnotherSubmission
														{...connect}
														message={message.dataMessage}
													/>
												)}
											</footer>
										</LayoutGap>
									</ToggleButton>
								</LayoutGap>
								{dataMessage.datovaZpravaId && dataMessageDataboxId ? (
									<MySubmissionAttachments
										code={code}
										messageId={dataMessage.datovaZpravaId}
										dataBoxId={dataMessageDataboxId}
										attachments={dataMessage.prilohy ?? []}
									/>
								) : null}

								{submission && isVoterRegistrationSubmission(submission) ? (
									<VoterRegistrationMessage
										submission={submission}
										onDownload={(onSuccess) =>
											powerOfAttorney.download(onSuccess)
										}
									/>
								) : null}
							</LayoutGap>
						) : null}
					</GovAccordionItem>
				</GovAccordion>
			</ContentLayout>
		</li>
	);
};
